import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { SchluesselZeile, StatsAnfrage } from '../../../lib/apiStatsApi';
import { deltaAnzeige, deltaFarbe, formatQuote, formatZeitpunkt } from './format';
import { nutzeSortierung, SortierKopf, type Spalte } from './Tabellen';
import SchluesselDetail from './SchluesselDetail';

interface SchluesselTabelleProps {
  zeilen: SchluesselZeile[];
  gekuerzt: boolean;
  anfrage: StatsAnfrage;
  darkMode: boolean;
  onEndpunktWaehlen: (route: string) => void;
}

export default function SchluesselTabelle({ zeilen, gekuerzt, anfrage, darkMode, onEndpunktWaehlen }: SchluesselTabelleProps) {
  const [offenId, setOffenId] = useState<number | null>(null);
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const spalten: Spalte<SchluesselZeile>[] = [
    { key: 'name', titel: 'Schlüssel', wert: z => z.name },
    { key: 'mandant', titel: 'Kunde', wert: z => z.mandant.name },
    { key: 'stufe', titel: 'Stufe', wert: z => z.stufe },
    { key: 'angelegt', titel: 'Angelegt', rechts: true, wert: z => z.angelegt, anzeige: z => formatZeitpunkt(z.angelegt) },
    { key: 'zugriff', titel: 'Letzter Zugriff', rechts: true, wert: z => z.letzterZugriff, anzeige: z => formatZeitpunkt(z.letzterZugriff) },
    { key: 'aufrufe', titel: 'Aufrufe', rechts: true, wert: z => z.aufrufe },
    { key: 'fehler', titel: 'Fehler', rechts: true, wert: z => z.fehler },
    { key: 'quote', titel: 'Quote', rechts: true, wert: z => z.fehlerquote, anzeige: z => formatQuote(z.fehlerquote) },
    {
      key: 'delta', titel: 'Δ Vorperiode', rechts: true,
      wert: z => (z.vorherAufrufe === 0 ? (z.aufrufe > 0 ? Number.POSITIVE_INFINITY : 0) : (z.aufrufe - z.vorherAufrufe) / z.vorherAufrufe),
      anzeige: z => {
        const d = deltaAnzeige(z.aufrufe, z.vorherAufrufe);
        return <span className={deltaFarbe(d.richtung, darkMode)}>{d.text}</span>;
      },
    },
  ];

  const { sortierung, sortiere, umschalten } = nutzeSortierung(spalten, 'aufrufe');

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[920px] text-sm">
        <SortierKopf spalten={spalten} sortierung={sortierung} umschalten={umschalten} darkMode={darkMode} />
        <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
          {sortiere(zeilen).map(z => (
            <Fragment key={z.id}>
              <tr
                onClick={() => setOffenId(offen => (offen === z.id ? null : z.id))}
                title="Details anzeigen"
                className={`cursor-pointer ${darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}`}
              >
                <td className="px-4 py-2 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {offenId === z.id ? <ChevronDown className="w-3.5 h-3.5 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                    {z.name}
                    <span className={`font-mono text-xs ${muted}`}>{z.prefix}…</span>
                    {z.widerrufen && (
                      <span className="px-1.5 rounded text-xs bg-red-100 text-red-700">widerrufen</span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2">{z.mandant.name}</td>
                <td className="px-4 py-2">{z.stufe}</td>
                <td className="px-4 py-2 text-right">{formatZeitpunkt(z.angelegt)}</td>
                <td className="px-4 py-2 text-right">{formatZeitpunkt(z.letzterZugriff)}</td>
                <td className="px-4 py-2 text-right">{z.aufrufe}</td>
                <td className={`px-4 py-2 text-right ${z.fehler > 0 ? 'text-red-500' : ''}`}>{z.fehler}</td>
                <td className="px-4 py-2 text-right">{formatQuote(z.fehlerquote)}</td>
                <td className="px-4 py-2 text-right">
                  {(() => { const d = deltaAnzeige(z.aufrufe, z.vorherAufrufe); return <span className={deltaFarbe(d.richtung, darkMode)}>{d.text}</span>; })()}
                </td>
              </tr>
              {offenId === z.id && (
                <tr>
                  <td colSpan={spalten.length} className={darkMode ? 'bg-slate-800/60' : 'bg-slate-50/60'}>
                    <SchluesselDetail id={z.id} anfrage={anfrage} darkMode={darkMode} onEndpunktWaehlen={onEndpunktWaehlen} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {zeilen.length === 0 && (
            <tr><td colSpan={spalten.length} className={`px-4 py-6 text-center ${muted}`}>Keine Schlüssel vorhanden</td></tr>
          )}
        </tbody>
      </table>
      {gekuerzt && (
        <p className={`px-4 py-2 text-xs ${muted}`}>Auf 500 Zeilen gekürzt — Filter enger stellen.</p>
      )}
    </div>
  );
}
